import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import type { Interaction, InteractionKind } from "./types";

export function useInteractions(leadId: string | null) {
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setInteractions([]);
      return;
    }
    let active = true;
    setLoading(true);
    void api<{ interactions: Interaction[] }>(
      `/api/leads/${encodeURIComponent(leadId)}/interactions`,
    )
      .then(({ interactions: initial }) => {
        if (active) setInteractions(initial);
      })
      .catch(() => {
        if (active) setInteractions([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    const events = new EventSource("/api/events");
    events.addEventListener("interaction", (event) => {
      if (!active) return;
      const payload = JSON.parse((event as MessageEvent).data) as {
        interaction?: Interaction & { deleted?: boolean };
      };
      const interaction = payload.interaction;
      if (!interaction || interaction.lead_id !== leadId) return;
      if (interaction.deleted) {
        setInteractions((current) =>
          current.filter((item) => item.id !== interaction.id),
        );
        return;
      }
      setInteractions((current) => {
        const rest = current.filter((item) => item.id !== interaction.id);
        const next = [...rest, interaction];
        next.sort(
          (a, b) =>
            b.happened_at.localeCompare(a.happened_at) ||
            b.created_at.localeCompare(a.created_at),
        );
        return next;
      });
    });
    return () => {
      active = false;
      events.close();
    };
  }, [leadId]);

  const add = useCallback(
    async (input: {
      content: string;
      happenedAt?: string;
      kind?: InteractionKind;
    }) => {
      const { interaction } = await api<{ interaction: Interaction }>(
        `/api/leads/${encodeURIComponent(leadId ?? "")}/interactions`,
        { method: "POST", body: JSON.stringify(input) },
      );
      setInteractions((current) => {
        const rest = current.filter((item) => item.id !== interaction.id);
        const next = [...rest, interaction];
        next.sort(
          (a, b) =>
            b.happened_at.localeCompare(a.happened_at) ||
            b.created_at.localeCompare(a.created_at),
        );
        return next;
      });
      return interaction;
    },
    [leadId],
  );

  const remove = useCallback(async (id: string) => {
    await api<{ ok: true }>(`/api/interactions/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
    setInteractions((current) => current.filter((item) => item.id !== id));
  }, []);

  return { interactions, loading, add, remove };
}
